import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import IconComponent from "../../cubeComponent/icons";
import { getUrlForImage } from "../../cubeComponent/constants/globalFunctions";
import { useSelector } from "react-redux";
import ActionCards from "./ActionCard";
import { getImageUrl } from "@Utils";
import Text from "@CubeComponents/html/Text/Text";

const iconTypeMap: { [key: string]: string } = {
  Transmittal: "Transmittals",
  Setting: "SettingsIcon",
  Space: "SpacesIcon",
  Issue: "IssuesIcon",
  File: "FilesIcon",
  Task: "TasksIcon",
  Estimate: "EstimateIcon",
  Review: "Review",
  Mailbox: "Mailbox",
  inspectionForm: "inspectionForm",
  billForm: "bills",
  progressUpdateForm: "ProgressUpdates",
  changeOrderForm: "changeOrder",
};

function ImageWithFallback({ imageUrl, defaultImage }: any) {
  const [imgSrc, setImgSrc] = useState<any>(imageUrl);
  const handleError = () => {
    setImgSrc(defaultImage);
  };

  return (
    <img
      className="w-6 rounded-full mx-1 h-6 min-w-12 min-h-12"
      src={imgSrc}
      onError={handleError}
      alt="projectImage"
    />
  );
}

function NotificationHeader({ t, count }: any) {
  return (
    <>
      <div>
        <ActionCards />
      </div>
      <hr className="my-2 border-blue-50" />
      <div className="flex items-center h-16 w-full">
        <div className="ps-8 py-4 font-semibold">
          <Text>{t("Notifications")}</Text>
        </div>
        <div className="inline-flex items-center p-1 ms-2 text-sm font-bold bg-[#ECF3FE] rounded">{count}</div>
      </div>
    </>
  );
}

function NotificationItem({
  notification,
  layout,
  t,
  isHovered,
  isLastInNew,
  isNewSection,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onDismiss,
}: any) {
  let cursorClass = "";

  if (isHovered) {
    cursorClass = notification.redirectUrl ? "cursor-pointer" : "cursor-not-allowed";
  }

  const borderClass = isLastInNew ? "" : "border-b border-[#ECF3FE]";

  return (
    <div className={`${borderClass} px-8 py-4`}>
      <div className="flex items-center justify-between">
        <button
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={`flex ${cursorClass}`}
          key={notification.id}
        >
          <div className="flex items-center">
            <div className="rounded-full bg-blue-100 w-12 h-12 flex items-center justify-center">
              {notification.type === "Project" ? (
                <ImageWithFallback
                  imageUrl={getUrlForImage({ id: `${notification.projectId}` }, "projectImage")}
                  defaultImage={`${getImageUrl() + layout.PROJECT_LOGO}`}
                />
              ) : (
                <IconComponent className="w-6 mx-1 h-6" name={iconTypeMap[notification.type] || "DefaultIcon"} />
              )}
            </div>
          </div>
          {/* --- 
            THIS IS THE FIX: 
            I've added the `text-start` class to force alignment. 
            I also added the `title` attribute as recommended before, 
            to show the full text on hover.
            ---
          */}
          <div className="grid grid-cols-[auto_1fr] items-center justify-between text-start">
            <p className="w-full whitespace-normal break-words px-6 text-sm" title={notification.text}>
              {notification.text}
            </p>
          </div>
        </button>

        <div className="flex items-center justify-end w-[7.5625rem]">
          <p className="text-gray-500 whitespace-nowrap text-xs">{notification.time}</p>
          {isNewSection && (
            <button onClick={onDismiss} className="ps-6 cursor-pointer" title={t("Mark as read")}>
              <Text>{t("✕")}</Text>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationCard(props: any) {
  const { t } = useTranslation();
  const layout = useSelector((state: any) => state.layout);
  const customModules = useSelector((state: any) => state.customModules);

  useEffect(() => {
    customModules.forEach((module: any) => {
      iconTypeMap[module.resourceType] = module.icon;
    });
  }, [customModules]);

  const unreadNotificationsCount = useSelector((state: any) => state.unreadNotificationsCount);
  const navigate = useNavigate();

  const [hoveredNotification, setHoveredNotification] = useState<any>(null);

  const handleMouseEnter = (notification: any) => {
    setHoveredNotification(notification.id);
  };

  const handleMouseLeave = () => {
    setHoveredNotification(null);
  };

  const handleRedirect = (redirectUrl: any) => {
    navigate(redirectUrl);
    props.closeNotificationModal();
  };

  const notificationClicked = (redirectUrl: any, notificationId: any) => {
    if (redirectUrl) {
      props.dismissNotification(notificationId);
      handleRedirect(redirectUrl);
    }
  };

  const handleDismiss = (e: any, notificationId: any) => {
    e.stopPropagation();
    props.dismissNotification(notificationId);
  };

  const isNewSection = props.title === "New";

  return (
    <div className="w-full">
      {isNewSection && <NotificationHeader t={t} count={unreadNotificationsCount.count} />}

      <div className="flex flex-col">
        <div
          className="bg-[#ECF3FE] text-black font-semibold flex px-8 text-base p-2 "
          hidden={!props.notificationsList.length}
        >
          {props.title}
        </div>

        <div className="flex w-full flex-col overflow-y-auto scroll-smooth">
          {props.notificationsList.map((notification: any, index: any) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              layout={layout}
              t={t}
              isHovered={hoveredNotification === notification.id}
              isLastInNew={index === 4 && isNewSection}
              isNewSection={isNewSection}
              onMouseEnter={() => handleMouseEnter(notification)}
              onMouseLeave={handleMouseLeave}
              onClick={() => notificationClicked(notification.redirectUrl, notification.id)}
              onDismiss={(e: any) => handleDismiss(e, notification.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default NotificationCard;
